import { createClient, type Session } from "@supabase/supabase-js";
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "~/lib/env";

/**
 * The browser's session owner (R22 T10, plan end state).
 *
 * **supabase-js holds the session, exactly as the native apps do.** The cookie
 * model went with the Next server at T18, and with it `@supabase/ssr` and the two
 * transports `resolveCaller` had to support. One transport, one refresh
 * implementation, and a token this app can hand to the API Worker as a bearer.
 *
 * This is the XSS surface the plan names as a risk: a token in localStorage is
 * readable by any script that manages to run here, which a HttpOnly cookie was
 * not. The mitigation is the Content-Security-Policy emitted at build
 * (vite.config.ts), which allows no inline script and no third-party origin.
 *
 * **It authenticates and nothing else.** No table is ever read through this
 * client: the RLS posture denies writes and the reads all belong to the API
 * Worker, so a browser that could query Postgres directly would be a second
 * command path, which §4 forbids.
 */
export const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    // Bookr never signs in through a redirect flow, so there is no fragment to
    // read back and this only risks misreading a URL the app put there itself.
    detectSessionInUrl: false,
  },
});

/** The access token for the next API call, refreshed by supabase-js if stale. */
export async function currentAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/**
 * Adopt the session the API Worker just issued.
 *
 * The auth routes hand back `{ accessToken, refreshToken }` in the body rather
 * than setting a cookie, so this is where a login becomes a stored session.
 * Handing it to supabase-js rather than storing it here is the point: refresh,
 * expiry and cross-tab sync are its problem, and they are exactly the problems
 * a hand-rolled token store gets wrong.
 */
export async function adoptSession(wire: {
  accessToken: string;
  refreshToken: string;
}): Promise<Session | null> {
  const { data } = await supabase.auth.setSession({
    access_token: wire.accessToken,
    refresh_token: wire.refreshToken,
  });
  return data.session;
}

/** Forget the session locally. The API's logout route is called alongside it. */
export async function forgetSession(): Promise<void> {
  await supabase.auth.signOut({ scope: "local" });
}
